import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';

/**
 * Billing data layer — credits, invoices, transactions and payment methods.
 * All endpoints live on the v4 DataCentral backend (`/partner/*`), the current
 * version. Read shapes are confirmed against the live API (note: the invoices
 * list returns rows under a top-level `results` key, not `result`).
 */

export interface CreditBalance {
  customer_id: number;
  total_deposited: number;
  total_used: number;
  total_remaining: number;
  accounts_count: number;
  has_low_balance: boolean;
  has_locked_wallet: boolean;
}

export interface Wallet {
  wallet_id?: string;
  currency?: string;
  balance?: number;
  status?: string;
}

export interface InvoiceSummary {
  invoice_id: string;
  invoice_description: string;
  invoice_status: string;
  invoice_subtotal: number;
  invoice_tax: number;
  invoice_total: number;
  invoice_duedate: string;
  invoice_created_at: string;
}

export interface InvoiceLineItem {
  invoice_item_id: number;
  invoice_item_name?: string;
  invoice_item_description?: string;
  service_name?: string;
  product_group_name?: string;
  usage_quantity?: number;
  usage_unit?: string;
  usage_price?: number;
  invoice_item_amount?: number;
  invoice_item_discount?: number;
  tax_percentage?: number;
  currency?: string;
  invoice_item_apply_startdate?: string | null;
  invoice_item_apply_enddate?: string | null;
}

export interface InvoiceCustomer {
  customer_id?: string | number;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  customer_province?: string;
  customer_country?: string;
}

export interface InvoiceDetail extends InvoiceSummary {
  invoice_items?: InvoiceLineItem[];
  customer?: InvoiceCustomer;
  invoice_currency?: string;
  invoice_paid_at?: string | null;
}

export interface CreditTransaction {
  transaction_id?: string | number;
  credit_id?: number;
  amount?: number;
  /** deposit | applied | refund (API returns `transaction_type`). */
  transaction_type?: string;
  /** pending | processing | completed | failed. */
  transaction_status?: string;
  /** Free-text note (API returns `note`). */
  note?: string;
  invoice_id?: number;
  created_at?: string;
}

export interface PaymentMethod {
  id: string;
  type?: string;
  brand?: string;
  last4?: string;
  cardholderName?: string;
  expMonth?: string;
  expYear?: string;
  imageUrl?: string;
  isDefault?: boolean;
  createdAt?: string;
}

/** Raw saved-method shape from the API (card data nested under `details`). */
interface RawPaymentMethod {
  id: number | string;
  type?: string;
  isDefault?: boolean;
  createdAt?: string;
  details?: {
    cardType?: string;
    lastFour?: string;
    cardholderName?: string;
    expirationMonth?: string;
    expirationYear?: string;
    imageUrl?: string;
  };
}

function normalizeMethod(m: RawPaymentMethod): PaymentMethod {
  return {
    id: String(m.id),
    type: m.type,
    brand: m.details?.cardType,
    last4: m.details?.lastFour,
    cardholderName: m.details?.cardholderName,
    expMonth: m.details?.expirationMonth,
    expYear: m.details?.expirationYear,
    imageUrl: m.details?.imageUrl,
    isDefault: m.isDefault,
    createdAt: m.createdAt,
  };
}

export const billingKeys = {
  all: ['billing'] as const,
  credits: ['billing', 'credits'] as const,
  invoices: (page: number, pageSize: number) => ['billing', 'invoices', page, pageSize] as const,
  invoice: (id: string) => ['billing', 'invoice', id] as const,
  transactions: (page: number, pageSize: number) =>
    ['billing', 'transactions', page, pageSize] as const,
  methods: ['billing', 'payment-methods'] as const,
};

/** True when an invoice no longer needs paying. */
export function isInvoicePaid(status: string): boolean {
  return ['paid', 'completed', 'confirmed'].includes(status.toLowerCase());
}

export function invoiceStatusTone(
  status: string,
): 'success' | 'warning' | 'danger' | 'neutral' {
  const s = status.toLowerCase();
  if (isInvoicePaid(s)) return 'success';
  if (s.includes('overdue') || s.includes('cancel')) return 'danger';
  if (s.includes('wait') || s.includes('pending') || s.includes('unpaid')) return 'warning';
  return 'neutral';
}

export function useCredits() {
  return useQuery({
    queryKey: billingKeys.credits,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/credits');
      const r = res.data?.result ?? {};
      return {
        balance: (r.balance ?? null) as CreditBalance | null,
        wallets: (r.wallets ?? []) as Wallet[],
      };
    },
    staleTime: 60_000,
    retry: 1,
  });
}

export interface InvoicesPage {
  invoices: InvoiceSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export function useInvoices(page = 1, pageSize = 10, options?: { enabled?: boolean }) {
  return useQuery<InvoicesPage>({
    queryKey: billingKeys.invoices(page, pageSize),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/invoices', {
        params: { page, pageSize },
      });
      const d = res.data ?? {};
      const invoices = (d.results ?? d.result?.results ?? d.result ?? []) as InvoiceSummary[];
      const total =
        d.pagination?.total ?? d.total ?? (Array.isArray(invoices) ? invoices.length : 0);
      return { invoices: Array.isArray(invoices) ? invoices : [], total, page, pageSize };
    },
    staleTime: 30_000,
  });
}

export function useInvoice(id: string | undefined) {
  return useQuery<InvoiceDetail | null>({
    queryKey: billingKeys.invoice(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const res = await dataCentralApi.get(`/partner/invoices/${id}`);
      return (res.data?.result ?? res.data?.data ?? null) as InvoiceDetail | null;
    },
    retry: 1,
  });
}

export interface TransactionsPage {
  transactions: CreditTransaction[];
  total: number;
}

export function useCreditTransactions(page = 1, pageSize = 10) {
  return useQuery<TransactionsPage>({
    queryKey: billingKeys.transactions(page, pageSize),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/credits/transactions', {
        params: { page, pageSize },
      });
      const r = res.data?.result ?? {};
      return {
        transactions: (r.transactions ?? []) as CreditTransaction[],
        total: r.total ?? res.data?.pagination?.total ?? 0,
      };
    },
    staleTime: 30_000,
  });
}

export function usePaymentMethods() {
  return useQuery<PaymentMethod[]>({
    queryKey: billingKeys.methods,
    queryFn: async () => {
      const res = await dataCentralApi.get('/partner/payments/methods');
      const raw = (res.data?.result?.methods ?? []) as RawPaymentMethod[];
      return raw.map(normalizeMethod);
    },
    staleTime: 60_000,
    retry: 1,
  });
}

function usePaymentMethodInvalidation() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: billingKeys.methods });
}

export function useSetDefaultPaymentMethod(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const invalidate = usePaymentMethodInvalidation();
  return useMutation({
    mutationFn: async (methodId: string) => {
      await dataCentralApi.patch(`/partner/payments/methods/${methodId}`, { makeDefault: true });
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeletePaymentMethod(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const invalidate = usePaymentMethodInvalidation();
  return useMutation({
    mutationFn: async (methodId: string) => {
      await dataCentralApi.delete(`/partner/payments/methods/${methodId}`);
    },
    ...options,
    onSuccess: (...args) => {
      invalidate();
      options?.onSuccess?.(...args);
    },
  });
}
